import React, { useContext, useEffect, useState } from 'react';

import {
    GetRoyaltyManagerExecutionRequestDetailsByIdInput,
    GetRoyaltyManagerExecutionRequestDetailsByIdOutput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import deleteTokens from '@amzn/meridian-tokens/base/icon/trash';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Loader from '@amzn/meridian/loader';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

import { useHistory } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import { EDITOR_ROLE_SET } from 'src/constants/role';
import { ROUTES } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';

import './RoyaltyManagerRequestDetailsModal.scss';

interface RoyaltyManagerRequestDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    requestId: number | null;
    onEdit?: (requestId: number) => void;
    onDelete?: (requestId: number) => void;
}

interface RequestDetails {
    executionRequestId: number;
    description: string;
    requestDate: string;
    requestedBy: string;
    statusName?: string;
    periodFrom?: string;
    periodTo?: string;
    priority?: number;
    isAllEntities?: boolean;
    isAllRegions?: boolean;
    isAllServices?: boolean;
    services?: string[];
    regions?: string[];
    territories?: string[];
    entities?: string[];
}

export const RoyaltyManagerRequestDetailsModal: React.FC<RoyaltyManagerRequestDetailsModalProps> = ({
    isOpen,
    onClose,
    requestId,
    onEdit,
    onDelete
}) => {
    const { setTopAlertMsg, userRole, userInfo } = useContext(AppContext);
    const [requestDetails, setRequestDetails] = useState<RequestDetails | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const history = useHistory();

    useEffect(() => {
        if (isOpen && requestId) {
            loadRequestDetails();
        } else {
            setRequestDetails(null);
        }
    }, [isOpen, requestId]);

    const loadRequestDetails = () => {
        if (!requestId) return;

        setIsLoading(true);

        contractConfigurationClient
            .getRoyaltyManagerExecutionRequestDetailsById(
                GetRoyaltyManagerExecutionRequestDetailsByIdInput.clone({
                    executionRequestId: requestId
                })
            )
            .subscribe(
                (response: GetRoyaltyManagerExecutionRequestDetailsByIdOutput) => {
                    // Extract the actual details from the nested response structure
                    const details = (response as any).executionRequestDetails;
                    if (details) {
                        // Map the API response fields to our interface
                        const mappedDetails: RequestDetails = {
                            executionRequestId: details.requestId,
                            description: details.description,
                            requestDate: details.requestDate,
                            requestedBy: details.requestedBy,
                            statusName: details.statusName,
                            periodFrom: details.periodFrom,
                            periodTo: details.periodTo,
                            priority: details.priority,
                            isAllEntities: details.isAllEntities,
                            isAllRegions: details.isAllRegions,
                            isAllServices: details.isAllServices,
                            services: details.services,
                            regions: details.regions,
                            territories: details.territories,
                            entities: details.entities
                        };
                        setRequestDetails(mappedDetails);
                    } else {
                        setRequestDetails(null);
                    }
                    setIsLoading(false);
                },
                (error: UnknownCoralError) => {
                    setTopAlertMsg(error.receivedException?.message || 'Failed to load request details.');
                    setIsLoading(false);
                }
            );
    };

    const formatValue = (value: any): string => {
        if (value === null || value === undefined) {
            return 'N/A';
        }
        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        }
        if (typeof value === 'string' && value.includes('T') && value.includes('Z')) {
            // ISO date string
            try {
                const date = new Date(value);
                return date.toLocaleString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZoneName: 'short'
                });
            } catch {
                return value.toString();
            }
        }
        if (typeof value === 'number' && value.toString().length > 10) {
            // Likely a timestamp
            return new Date(value * 1000).toLocaleString();
        }
        return value.toString();
    };

    const getPriorityLabel = (priority: number): string => {
        switch (priority) {
            case 1:
                return 'High (1)';
            case 2:
                return 'Medium (2)';
            case 3:
                return 'Low (3)';
            default:
                return `Priority ${priority}`;
        }
    };

    const getStatusColor = (status: string): string => {
        const statusColors = {
            COMPLETED: '#4caf50',
            PENDING: '#ff9800',
            IN_PROGRESS: '#2196f3',
            FAILED: '#f44336',
            CANCELLED: '#9e9e9e',
            QUEUED: '#9c27b0'
        };
        return statusColors[status?.toUpperCase() as keyof typeof statusColors] || '#9e9e9e';
    };

    const renderListValue = (items: string[] | undefined, maxDisplay = 5) => {
        if (!items || !Array.isArray(items) || items.length === 0) {
            return (
                <Text type="b200" color="secondary">
                    None specified
                </Text>
            );
        }

        const displayItems = items.slice(0, maxDisplay);
        const hasMore = items.length > maxDisplay;

        return (
            <Column spacing="100">
                {displayItems.map((item, index) => (
                    <Text key={index} type="b200">
                        • {item}
                    </Text>
                ))}
                {hasMore && (
                    <Text type="b200" color="secondary">
                        ... and {items.length - maxDisplay} more
                    </Text>
                )}
                <Text type="b100" color="secondary">
                    Total: {items.length} items
                </Text>
            </Column>
        );
    };

    const renderDetailRow = (label: string, value: any, isStatus = false, isPriority = false, isList = false) => (
        <Row alignmentVertical="top" spacing="200">
            <Column width="30%">
                <Text type="b300" color="secondary">
                    {label}:
                </Text>
            </Column>
            <Column width="70%">
                {isStatus && value ? (
                    <span className="status-badge" style={{ backgroundColor: getStatusColor(value) }}>
                        {value}
                    </span>
                ) : isPriority && value !== null && value !== undefined ? (
                    <span className="priority-badge" style={{ backgroundColor: value === 1 ? '#f44336' : '#4caf50' }}>
                        {getPriorityLabel(value)}
                    </span>
                ) : isList ? (
                    renderListValue(value)
                ) : (
                    <Text type="b200">{formatValue(value)}</Text>
                )}
            </Column>
        </Row>
    );

    // Define which fields are lists
    const listFields = ['services', 'regions', 'territories', 'entities'];

    // Define which fields should be grouped together
    const basicFields = ['executionRequestId', 'description', 'requestedBy', 'requestDate', 'statusName'];
    const parameterFields = ['periodFrom', 'periodTo', 'priority', 'isAllEntities', 'isAllRegions', 'isAllServices'];

    // Check if user can modify this request (same conditions as in main component)
    const canModify =
        requestDetails &&
        requestDetails.requestedBy === userInfo.username &&
        EDITOR_ROLE_SET.has(userRole) &&
        requestDetails.statusName?.toUpperCase() === 'PENDING';

    const handleEdit = () => {
        if (requestId && onEdit) {
            onEdit(requestId);
            onClose(); // Close details modal when opening edit modal
        }
    };

    const handleDelete = () => {
        if (requestId && onDelete) {
            onDelete(requestId);
            onClose(); // Close details modal after delete
        }
    };

    return (
        <Modal open={isOpen} onClose={onClose} title={`Request Details ${requestId ? `- ID: ${requestId}` : ''}`}>
            <div className="create-royalty-request-modal-content">
                {/* Content */}
                {isLoading ? (
                    <Column alignmentHorizontal="center" alignmentVertical="center" height="300px">
                        <Loader size="large" />
                        <Text type="b200" color="secondary">
                            Loading request details...
                        </Text>
                    </Column>
                ) : requestDetails ? (
                    <Column spacing="300">
                        {/* Basic Information */}
                        <Box type="outline" spacingInset="300">
                            <Column spacing="200">
                                <Text type="h300">Basic Information</Text>
                                {renderDetailRow('Request ID', requestDetails.executionRequestId)}
                                {renderDetailRow('Description', requestDetails.description)}
                                {renderDetailRow('Requested By', requestDetails.requestedBy)}
                                {renderDetailRow('Request Date', requestDetails.requestDate)}
                                {renderDetailRow('Status', requestDetails.statusName, true)}
                            </Column>
                        </Box>

                        {/* Execution Parameters */}
                        <Box type="outline" spacingInset="300">
                            <Column spacing="200">
                                <Text type="h300">Execution Parameters</Text>
                                {renderDetailRow('Period From', requestDetails.periodFrom)}
                                {renderDetailRow('Period To', requestDetails.periodTo)}
                                {renderDetailRow('Priority', requestDetails.priority, false, true)}
                                {renderDetailRow('All Entities', requestDetails.isAllEntities)}
                                {renderDetailRow('All Regions', requestDetails.isAllRegions)}
                                {renderDetailRow('All Services', requestDetails.isAllServices)}
                            </Column>
                        </Box>

                        {/* Execution Specifics */}
                        <Box type="outline" spacingInset="300">
                            <Column spacing="300">
                                <Text type="h300">Execution Specifics</Text>

                                {renderDetailRow(
                                    'Services',
                                    requestDetails.isAllServices ? ['All Services'] : requestDetails.services || [],
                                    false,
                                    false,
                                    true
                                )}

                                {renderDetailRow(
                                    'Regions',
                                    requestDetails.isAllRegions ? ['All Regions'] : requestDetails.regions || [],
                                    false,
                                    false,
                                    true
                                )}

                                {requestDetails.territories &&
                                    requestDetails.territories.length > 0 &&
                                    renderDetailRow('Territories', requestDetails.territories, false, false, true)}

                                {renderDetailRow(
                                    'Entities',
                                    requestDetails.isAllEntities ? ['All Entities'] : requestDetails.entities || [],
                                    false,
                                    false,
                                    true
                                )}
                            </Column>
                        </Box>

                        {/* Additional Details */}
                        {Object.keys(requestDetails).some(
                            (key) => ![...basicFields, ...parameterFields, ...listFields].includes(key)
                        ) && (
                            <Box type="outline" spacingInset="300">
                                <Column spacing="200">
                                    <Text type="h300">Additional Details</Text>
                                    {Object.entries(requestDetails)
                                        .filter(
                                            ([key]) =>
                                                ![...basicFields, ...parameterFields, ...listFields].includes(key)
                                        )
                                        .map(([key, value]) => (
                                            <div key={key}>
                                                {renderDetailRow(
                                                    key
                                                        .replace(/([A-Z])/g, ' $1')
                                                        .replace(/^./, (str) => str.toUpperCase()),
                                                    value,
                                                    false,
                                                    false,
                                                    Array.isArray(value)
                                                )}
                                            </div>
                                        ))}
                                </Column>
                            </Box>
                        )}
                    </Column>
                ) : (
                    <Column alignmentHorizontal="center" alignmentVertical="center" height="300px">
                        <Text type="b200" color="secondary">
                            No details available
                        </Text>
                    </Column>
                )}
            </div>

            <ModalFooter>
                <Row alignmentHorizontal="end">
                    {/* Action buttons on the left (only show if user can modify) */}
                    {canModify && (
                        <Row spacing="200">
                            <Button type="link" size="medium" onClick={handleEdit}>
                                <Icon tokens={editTokens} />
                                Edit
                            </Button>
                            <Button type="link" size="medium" onClick={handleDelete}>
                                <Icon tokens={deleteTokens} />
                                Delete
                            </Button>
                        </Row>
                    )}

                    {requestDetails?.statusName === 'COMPLETED' && (
                        <Button
                            type="primary"
                            onClick={() =>
                                history.push(`${ROUTES.ROYALTY_MANAGER.INDEX}/${requestDetails.executionRequestId}`)
                            }
                        >
                            See execution details
                        </Button>
                    )}

                    {/* Close button on the right */}
                    <Button type="secondary" onClick={onClose}>
                        Close
                    </Button>
                </Row>
            </ModalFooter>
        </Modal>
    );
};
